import { FC } from 'react';

import CtaButton from '@components/quiz/shared/CtaButton';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import type { SanityReadinessTeamConfirmBlock } from '@lib/sanity/types';

type TeamConfirmBlockProps = SanityReadinessTeamConfirmBlock & {
  onConfirm: () => void;
  onSearchAgain: () => void;
};

/**
 * Step 2a: confirm the team picked from search. The second cell prefers
 * the GW score (post-GW1, when gwScoreLabel is configured), then the
 * overall rank, then the manager name (pre-GW1, where neither exists).
 */
const TeamConfirmBlock: FC<TeamConfirmBlockProps> = ({
  foundBadgeText,
  teamIdLabel,
  managerLabel,
  rankLabel,
  gwScoreLabel,
  confirmCtaText,
  searchAgainLinkText,
  onConfirm,
  onSearchAgain
}) => {
  const { state } = useReadiness();
  const { teamMeta } = state;

  if (!teamMeta) return null;

  const isConfirmDisabled =
    state.isImporting || state.team.length === 0 || Boolean(state.importError);

  return (
    <section className="flex min-h-[60vh] flex-col items-center justify-between px-6 pb-10 pt-8">
      <div className="w-full max-w-sm">
        <div className="rounded-xl border border-neutral-200 bg-neutral-100 p-5">
          <div className="flex items-start justify-between gap-3">
            <p className="font-heading text-lg font-bold text-neutral-800">
              {teamMeta.teamName}
            </p>
            <span className="shrink-0 rounded-full bg-success-50 px-2.5 py-1 font-body text-[10px] font-bold tracking-widest text-success-600">
              {foundBadgeText ?? 'FOUND'}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="font-body text-[10px] font-bold uppercase tracking-widest text-neutral-500">
                {teamIdLabel ?? 'TEAM ID'}
              </p>
              <p className="mt-0.5 font-heading text-sm font-bold text-neutral-800">
                {teamMeta.fplId}
              </p>
            </div>
            {gwScoreLabel && teamMeta.gameweekPoints !== null ? (
              <div>
                <p className="font-body text-[10px] font-bold uppercase tracking-widest text-neutral-500">
                  {gwScoreLabel}
                </p>
                <p
                  className="mt-0.5 font-heading text-sm font-bold text-neutral-800"
                  data-cy="readiness-confirm-gw-score"
                >
                  {teamMeta.gameweekPoints} pts
                </p>
              </div>
            ) : teamMeta.overallRank !== null ? (
              <div>
                <p className="font-body text-[10px] font-bold uppercase tracking-widest text-neutral-500">
                  {rankLabel ?? 'OVERALL RANK'}
                </p>
                <p className="mt-0.5 font-heading text-sm font-bold text-neutral-800">
                  {teamMeta.overallRank.toLocaleString()}
                </p>
              </div>
            ) : (
              <div>
                <p className="font-body text-[10px] font-bold uppercase tracking-widest text-neutral-500">
                  {managerLabel ?? 'MANAGER'}
                </p>
                <p className="mt-0.5 font-heading text-sm font-bold text-neutral-800">
                  {teamMeta.managerName}
                </p>
              </div>
            )}
          </div>
        </div>

        {state.importError && (
          <p className="mt-3 font-body text-sm text-danger-600">
            {state.importError}
          </p>
        )}
      </div>

      <div className="mt-10 w-full max-w-sm">
        <CtaButton
          onClick={onConfirm}
          disabled={isConfirmDisabled}
          dataCy="readiness-confirm-team-cta"
        >
          {state.isImporting
            ? 'Loading your squad…'
            : confirmCtaText ?? 'Confirm team'}
        </CtaButton>
        <button
          type="button"
          onClick={onSearchAgain}
          data-cy="readiness-search-again-link"
          className="mt-3 w-full text-center font-body text-sm font-semibold text-primary-500 transition-colors hover:text-primary-600"
        >
          {searchAgainLinkText ?? 'Not your team? Search again'}
        </button>
      </div>
    </section>
  );
};

export default TeamConfirmBlock;
